import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Modal, Button, Form, Badge } from "react-bootstrap";
import { FiPlus, FiEdit2, FiTrash2 } from "react-icons/fi";
import ConfirmModal from "../components/ConfirmModal";
import api from "../services/api";

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [search, setSearch] = useState("");
  const [nameSuggestion, setNameSuggestion] = useState(null);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
  });
  const [loyaltyRules, setLoyaltyRules] = useState([]);
  const [expandedCustomer, setExpandedCustomer] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 20;
  const [sortKey, setSortKey] = useState("name");
  const [sortDir, setSortDir] = useState("asc");
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: "",
    message: "",
    onConfirm: null,
  });

  const openConfirm = (title, message, onConfirm) => {
    setConfirmModal({ show: true, title, message, onConfirm });
  };

  const closeConfirm = () => {
    setConfirmModal((prev) => ({ ...prev, show: false, onConfirm: null }));
  };

  useEffect(() => {
    loadCustomers();
    loadLoyaltyRules();
  }, []);

  const loadLoyaltyRules = async () => {
    try {
      const res = await api.get("/settings/loyalty-rules");
      setLoyaltyRules(res.data.filter((r) => r.is_active));
    } catch (error) {
      /* ignore */
    }
  };

  const loadCustomers = async () => {
    try {
      const res = await api.get("/customers", { params: { search } });
      setCustomers(res.data);
      setCurrentPage(1);
    } catch (error) {
      toast.error("Gagal memuat data");
    }
  };

  const handleSubmit = async () => {
    if (!form.name) {
      toast.error("Nama wajib diisi");
      return;
    }
    try {
      if (editItem) {
        await api.put(`/customers/${editItem.id}`, form);
        toast.success("Customer berhasil diupdate");
      } else {
        await api.post("/customers", form);
        toast.success("Customer berhasil ditambahkan");
      }
      setShowModal(false);
      setEditItem(null);
      setNameSuggestion(null);
      setForm({ name: "", phone: "", email: "" });
      loadCustomers();
    } catch (error) {
      if (error.response?.status === 409 && error.response?.data?.suggested_name) {
        setNameSuggestion(error.response.data.suggested_name);
      } else {
        toast.error(error.response?.data?.message || "Gagal menyimpan");
      }
    }
  };

  const handleEdit = (c) => {
    setEditItem(c);
    setForm({
      name: c.name,
      phone: c.phone || "",
      email: c.email || "",
    });
    setShowModal(true);
  };

  const handleDeleteCustomer = (id, name) => {
    openConfirm("Hapus Customer", `Hapus customer "${name}"?`, async () => {
      closeConfirm();
      try {
        await api.delete(`/customers/${id}`);
        toast.success("Customer berhasil dihapus");
        loadCustomers();
      } catch (error) {
        toast.error("Gagal menghapus customer");
      }
    });
  };

  const fmt = (n) => "Rp " + Number(n || 0).toLocaleString("id-ID");

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortKey(key);
      setSortDir("asc");
    }
    setCurrentPage(1);
  };

  const sortIndicator = (key) => {
    if (sortKey !== key) return " ↕";
    return sortDir === "asc" ? " ↑" : " ↓";
  };

  const sortedCustomers = [...customers].sort((a, b) => {
    let va = a[sortKey], vb = b[sortKey];
    if (sortKey === "name") {
      va = (va || "").toLowerCase();
      vb = (vb || "").toLowerCase();
      return sortDir === "asc" ? va.localeCompare(vb) : vb.localeCompare(va);
    }
    va = Number(va || 0);
    vb = Number(vb || 0);
    return sortDir === "asc" ? va - vb : vb - va;
  });

  const totalPages = Math.max(1, Math.ceil(sortedCustomers.length / PAGE_SIZE));
  const pagedCustomers = sortedCustomers.slice(
    (currentPage - 1) * PAGE_SIZE,
    currentPage * PAGE_SIZE
  );

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h4 className="mb-0">Manajemen Customer</h4>
        <button
          className="btn btn-primary"
          onClick={() => {
            setEditItem(null);
            setForm({ name: "", phone: "", email: "" });
            setShowModal(true);
          }}
        >
          + Tambah Customer
        </button>
      </div>

      <div className="card p-3 mb-3">
        <div className="row g-2">
          <div className="col-md-4">
            <input
              type="text"
              className="form-control"
              placeholder="Cari nama atau HP..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadCustomers()}
            />
          </div>
          <div className="col-md-2">
            <button className="btn btn-primary" onClick={loadCustomers}>
              Cari
            </button>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="table-responsive">
          <table className="table table-hover mb-0">
            <thead>
              <tr>
                <th style={{ cursor: "pointer" }} onClick={() => handleSort("name")}>Nama{sortIndicator("name")}</th>
                <th>HP</th>
                <th>Loyalti</th>
                <th className="text-end" style={{ cursor: "pointer" }} onClick={() => handleSort("total_transactions")}>Total Transaksi{sortIndicator("total_transactions")}</th>
                <th className="text-end" style={{ cursor: "pointer" }} onClick={() => handleSort("total_spent")}>Total Belanja{sortIndicator("total_spent")}</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {pagedCustomers.map((c) => (
                <React.Fragment key={c.id}>
                  <tr>
                    <td className="fw-bold">{c.name}</td>
                    <td>{c.phone || "-"}</td>
                    <td>
                      {(c.loyalty_progress || []).length > 0 ? (
                        <div>
                          {c.loyalty_progress.map((lp) => (
                            <div key={lp.rule_id} className="small">
                              {lp.rule_name}:{" "}
                              <strong>{lp.current_count}</strong>/
                              {lp.required_count}
                              {lp.current_count >= lp.required_count && (
                                <Badge
                                  bg="success"
                                  className="ms-1"
                                  style={{ fontSize: "0.6rem" }}
                                >
                                  REDEEM
                                </Badge>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <span className="text-muted small">-</span>
                      )}
                    </td>
                    <td className="text-end">{c.total_transactions || 0}x</td>
                    <td className="text-end">{fmt(c.total_spent)}</td>
                    <td>
                      <div className="d-flex gap-1">
                        <button
                          className="btn btn-sm btn-outline-primary"
                          onClick={() => handleEdit(c)}
                          title="Edit Customer"
                        >
                          <FiEdit2 size={14} />
                        </button>
                        <button
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => handleDeleteCustomer(c.id, c.name)}
                          title="Hapus Customer"
                        >
                          <FiTrash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                </React.Fragment>
              ))}
              {customers.length === 0 && (
                <tr>
                  <td colSpan="8" className="text-center text-muted py-4">
                    Belum ada data
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="d-flex justify-content-between align-items-center px-3 py-2 border-top">
            <small className="text-muted">
              Menampilkan {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, customers.length)} dari {customers.length} customer
            </small>
            <ul className="pagination pagination-sm mb-0">
              <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>‹</button>
              </li>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter(p => p === 1 || p === totalPages || Math.abs(p - currentPage) <= 2)
                .reduce((acc, p, idx, arr) => {
                  if (idx > 0 && p - arr[idx - 1] > 1) acc.push("...");
                  acc.push(p);
                  return acc;
                }, [])
                .map((p, idx) =>
                  p === "..." ? (
                    <li key={`ellipsis-${idx}`} className="page-item disabled"><span className="page-link">…</span></li>
                  ) : (
                    <li key={p} className={`page-item ${currentPage === p ? "active" : ""}`}>
                      <button className="page-link" onClick={() => setCurrentPage(p)}>{p}</button>
                    </li>
                  )
                )}
              <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>›</button>
              </li>
            </ul>
          </div>
        )}
      </div>

      {/* Customer Modal */}
      <Modal show={showModal} onHide={() => { setShowModal(false); setNameSuggestion(null); }} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            {editItem ? "Edit Customer" : "Tambah Customer"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Nama *</Form.Label>
            <Form.Control
              value={form.name}
              onChange={(e) => { setForm({ ...form, name: e.target.value }); setNameSuggestion(null); }}
              isInvalid={!!nameSuggestion}
            />
            {nameSuggestion && (
              <div className="mt-1">
                <small className="text-danger">Nama sudah ada. Saran: <strong>{nameSuggestion}</strong></small>
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 ms-2"
                  onClick={() => { setForm({ ...form, name: nameSuggestion }); setNameSuggestion(null); }}
                >
                  Gunakan nama ini
                </button>
              </div>
            )}
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>No. HP</Form.Label>
            <Form.Control
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Email</Form.Label>
            <Form.Control
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Batal
          </Button>
          <Button variant="primary" onClick={handleSubmit}>
            Simpan
          </Button>
        </Modal.Footer>
      </Modal>

      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
      />
    </div>
  );
};

export default Customers;
